import React, { useEffect, useState } from "react";
import { Form, Upload, message } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import CommonDialog from "../../../../global/dialog";
import CommonForm from "../../../../global/form";
import SOURCES from "../../../../../utils/constants";
import { useGenericMutation } from "../../../../../queries/use-generic-mutation";

const { Dragger } = Upload;

const normalizeHeader = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const pickField = (row, aliases) => {
  const entries = Object.entries(row || {});
  for (const alias of aliases) {
    const match = entries.find(([key]) => normalizeHeader(key) === alias);
    if (match && match[1] !== undefined && match[1] !== null && match[1] !== "") {
      return String(match[1]).trim();
    }
  }
  return "";
};

const toProxyRecord = (row) => {
  const host = pickField(row, ["proxyaddress", "host", "ip", "ipaddress", "address"]);
  const port = Number(pickField(row, ["port", "proxyport"]));
  const login = pickField(row, ["username", "user", "login", "userlogin"]);
  const password = pickField(row, ["password", "pass", "pwd"]);

  if (!host || !port || !login || !password) return null;
  return { host, port, login, password };
};

const parseTxtLines = (text) => {
  const proxies = [];
  const errors = [];

  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line, index) => {
      const parts = line.includes(",")
        ? line.split(",").map((p) => p.trim())
        : line.split(":").map((p) => p.trim());

      if (parts.length < 4) {
        errors.push(`Line ${index + 1}: expected ip:port:username:password`);
        return;
      }

      const [host, port, login, ...passwordParts] = parts;
      const password = passwordParts.join(":");
      const parsedPort = Number(port);

      if (!host || !login || !password || !parsedPort) {
        errors.push(`Line ${index + 1}: invalid ip/port/username/password`);
        return;
      }

      proxies.push({ host, port: parsedPort, login, password });
    });

  return { proxies, errors };
};

const parseCsv = (text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return { proxies: [], errors: ["CSV file is empty"] };

  if (!/[a-zA-Z]/.test(lines[0].split(/,|\t/)[0] || "") || lines[0].includes(":")) {
    return parseTxtLines(text);
  }

  const delimiter = lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/^"|"$/g, ""));
  const proxies = [];
  const errors = [];

  lines.slice(1).forEach((line, index) => {
    const cols = line.split(delimiter).map((c) => c.trim().replace(/^"|"$/g, ""));
    const row = {};
    headers.forEach((header, i) => {
      row[header] = cols[i] ?? "";
    });

    const proxy = toProxyRecord(row);
    if (!proxy) {
      errors.push(`Row ${index + 2}: missing proxy_address/port/username/password`);
      return;
    }
    proxies.push(proxy);
  });

  return { proxies, errors };
};

const parseJson = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { proxies: [], errors: ["Invalid JSON file"] };
  }

  const rows = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.results)
      ? parsed.results
      : Array.isArray(parsed?.proxies)
        ? parsed.proxies
        : Array.isArray(parsed?.data)
          ? parsed.data
          : null;

  if (!rows) {
    return {
      proxies: [],
      errors: ["JSON must be an array (or { results: [] } / { proxies: [] })"],
    };
  }

  const proxies = [];
  const errors = [];

  rows.forEach((row, index) => {
    const proxy = toProxyRecord(row);
    if (!proxy) {
      errors.push(`Item ${index + 1}: missing proxy_address/port/username/password`);
      return;
    }
    proxies.push(proxy);
  });

  return { proxies, errors };
};

const parseUploadedFile = async (file) => {
  const text = await file.text();
  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".json")) return parseJson(text);
  if (name.endsWith(".csv")) return parseCsv(text);
  if (name.endsWith(".txt")) return parseTxtLines(text);

  const trimmed = text.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return parseJson(text);
  if (trimmed.includes(",") && /proxy_address|username|port/i.test(trimmed.split("\n")[0] || "")) {
    return parseCsv(text);
  }
  return parseTxtLines(text);
};

const EnhancedBulkUploadStaticResidentialIp = ({ open, setOpen, onSuccess }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [parsedCount, setParsedCount] = useState(0);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileList([]);
      setParsedCount(0);
    }
  }, [open, form]);

  const bulkMutation = useGenericMutation({
    endpoint: "admin/proxies/bulk",
    method: "post",
    onSuccess: (res) => {
      const payload = res?.data || {};
      if (payload.success === false) {
        message.warning(payload.message || "Bulk upload partially failed");
      } else {
        message.success(payload.message || "Proxies added to the pool");
      }
      setOpen(false);
      form.resetFields();
      setFileList([]);
      setParsedCount(0);
      onSuccess?.();
    },
  });

  const beforeUpload = async (file) => {
    const allowed = /\.(csv|json|txt)$/i.test(file.name);
    if (!allowed) {
      message.error("Upload a Webshare .csv, .json, or .txt file");
      return Upload.LIST_IGNORE;
    }

    try {
      const { proxies, errors } = await parseUploadedFile(file);
      if (!proxies.length) {
        message.error(errors[0] || "No valid proxies found in file");
        return Upload.LIST_IGNORE;
      }
      if (errors.length) {
        message.warning(`Parsed ${proxies.length} proxies. Skipped ${errors.length} invalid row(s).`);
      } else {
        message.success(`Ready to upload ${proxies.length} proxies`);
      }
      file.__parsedProxies = proxies;
      setParsedCount(proxies.length);
      setFileList([file]);
    } catch (err) {
      message.error(err?.message || "Failed to read file");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const file = fileList[0];
      const proxies = file?.__parsedProxies || [];

      if (!proxies.length) {
        message.error("Upload a Webshare CSV/JSON/TXT file first");
        return;
      }

      const payload = proxies.map((proxy) => ({
        ...proxy,
        type: values.type || "http",
        provider: values.provider || "webshare",
      }));

      await bulkMutation.mutateAsync({ proxies: payload });
    } catch (err) {
      if (err?.errorFields) return;
      console.log(err);
    }
  };

  return (
    <CommonDialog
      open={open}
      setOpen={setOpen}
      title="Bulk Upload Static Residential Proxies"
      description="Upload the file you downloaded from Webshare (.csv, .json, or .txt)."
      okButtonTitle={parsedCount ? `Upload ${parsedCount} proxies` : "Upload"}
      isConfirmButtonLoading={bulkMutation.isLoading}
      onOk={handleSubmit}
      onCancel={() => {
        setOpen(false);
        form.resetFields();
        setFileList([]);
        setParsedCount(0);
      }}
    >
      <div className="space-y-4 mt-2">
        <Dragger
          accept=".csv,.json,.txt"
          maxCount={1}
          fileList={fileList}
          beforeUpload={beforeUpload}
          onRemove={() => {
            setFileList([]);
            setParsedCount(0);
          }}
          className="!bg-primary/5 hover:!bg-primary/10 !border-primary/40 !rounded-xl"
        >
          <div className="py-6 flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CloudUploadOutlined style={{ fontSize: 32, color: "var(--primary-color, #2dbdf2)" }} />
            </div>
            <div className="text-center px-4">
              <p className="text-base font-semibold text-gray-800 mb-1">
                Drop Webshare file here, or click to browse
              </p>
              <p className="text-sm text-gray-500 mb-0">
                .txt / .csv / .json — ip:port:username:password
              </p>
            </div>
          </div>
        </Dragger>

        <CommonForm
          form={form}
          data={[
            {
              label: "Type",
              name: "type",
              inputType: SOURCES.FORMINPUTTYPES.SELECT,
              placeholder: "Select type",
              required: false,
              options: [
                { label: "HTTP", value: "http" },
                { label: "HTTPS", value: "https" },
                { label: "SOCKS5", value: "socks5" },
              ],
              span: 12,
            },
            {
              label: "Provider",
              name: "provider",
              inputType: SOURCES.FORMINPUTTYPES.INPUT,
              type: "text",
              placeholder: "webshare",
              required: false,
              span: 12,
            },
          ]}
        />
      </div>
    </CommonDialog>
  );
};

export default EnhancedBulkUploadStaticResidentialIp;
